/**
 * LSP-17745 — scroll-range arithmetic for a horizontally scrollable tab strip.
 *
 * Split out of EntityDetailPage.vue so the range maths is testable on its own: jsdom has no
 * layout engine, so `scrollWidth` / `clientWidth` on a mounted component are always 0 and a test
 * driven through the DOM could only ever assert the degenerate case. These take a plain shape
 * instead, which a test can populate with the geometry a real strip reports.
 *
 * The tolerance mirrors EntityDetailPage's own `checkTabsScrollPosition`: browsers report
 * fractional scroll offsets on high-DPI screens, and a sub-pixel overshoot is not a defect —
 * treating it as one would rewrite `scrollLeft` on every zoom frame for no visible gain.
 */

/** The parts of an `Element` this module reads. Anything with these three numbers will do. */
export interface HorizontalScroller {
	scrollLeft: number;
	scrollWidth: number;
	clientWidth: number;
}

/** Sub-pixel overshoot to ignore, in CSS pixels. */
export const SCROLL_RANGE_TOLERANCE_PX = 1;

/**
 * The largest in-range `scrollLeft`. Floored at 0 so a strip that no longer overflows reports 0
 * rather than a negative bound — that case is the one where a stale offset is most visible,
 * because every pixel of the strip past the last tab is empty.
 */
export function maxScrollLeft(scroller: HorizontalScroller): number {
	return Math.max(scroller.scrollWidth - scroller.clientWidth, 0);
}

/**
 * True when the strip is parked beyond the end of its own content — the state that renders as
 * dead space to the right of the last tab. Deliberately one-sided: a negative `scrollLeft` is
 * rubber-band overscroll, which the browser undoes on its own.
 */
export function isScrolledPastContent(scroller: HorizontalScroller): boolean {
	return scroller.scrollLeft - maxScrollLeft(scroller) > SCROLL_RANGE_TOLERANCE_PX;
}

/** Fraction of the visible width one chevron press advances (LSP-16975). */
export const SCROLL_NUDGE_FRACTION = 0.75;

/** Floor for that nudge, so a very narrow strip still moves usefully (LSP-16975 — do not remove). */
export const MIN_SCROLL_NUDGE_PX = 120;

/**
 * Absolute target for one chevron press, already clamped to the scrollable range.
 *
 * LSP-17745: the chevron used to hand the engine a RELATIVE `scrollBy` and rely on it to stop at the
 * content edge. Desktop engines do; iOS Safari does not clamp an animated relative scroll reliably,
 * so a press near the end lands past the last tab and stays there — the last tab ends up on the left
 * with dead strip to its right. Deriving the absolute target here makes the end stop
 * engine-independent: a press can reveal the rest of the tabs, never emptiness beyond them.
 *
 * `direction`: -1 toward earlier tabs, +1 toward later tabs.
 */
export function nextScrollTarget(scroller: HorizontalScroller, direction: number): number {
	const nudge = Math.max(Math.floor(scroller.clientWidth * SCROLL_NUDGE_FRACTION), MIN_SCROLL_NUDGE_PX);
	const unclamped = scroller.scrollLeft + direction * nudge;
	return Math.min(Math.max(unclamped, 0), maxScrollLeft(scroller));
}
